import { Button } from '@zep-us/zus';
import { ChevronRightIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import type { CategoryTreeSubjectNameData } from '@/api/gen/_model/categoryTreeSubjectNameData';
import type { QuizsetFilterCriteria } from '@/api/gen/_model/quizsetFilterCriteria';
import { usePublicCategoryState } from '@/module/public/hooks';
import { SchoolMapper } from '@/shared/category/util';
import { QuizListItemWithActions } from '../../quiz-list-item/components/QuizListItemWithActions';
import { useCategoryQuizzes } from '../hooks/useCategoryQuizzes';
import { useCategoryTitle } from '../hooks/useCategoryTitle';
interface CategoryQuizzesSectionProps {
  /** 카테고리 필터 조건 (학년, 과목, 출판사 등) */
  criteria: Partial<QuizsetFilterCriteria>;
  /** 과목 필터 */
  subject?: string;
  /** 카테고리 트리 데이터 (제목 표시용) */
  categoryTreeData?: CategoryTreeSubjectNameData;
  /** 현재 퀴즈 ID (결과에서 제외하기 위해) */
  currentQuizId: string;
}

/**
 * CategoryQuizzesSection - 같은 카테고리 퀴즈 섹션
 *
 * @description
 * 현재 퀴즈와 동일한 카테고리(학년/과목/출판사)의 다른 퀴즈 목록을
 * 세로 형태로 표시합니다. 로딩, 에러, 빈 상태를 처리합니다.
 *
 * @example
 * ```tsx
 * <CategoryQuizzesSection
 *   criteria={{ schoolGrades: ['중1'], publishers: ['비상'] }}
 *   subject="수학"
 *   currentQuizId="quiz-456"
 * />
 * ```
 */
export const CategoryQuizzesSection = ({
  criteria,
  subject,
  categoryTreeData,
  currentQuizId
}: CategoryQuizzesSectionProps) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const [, setCategoryState] = usePublicCategoryState();
  const categoryTitle = useCategoryTitle(categoryTreeData);
  const {
    data,
    isLoading,
    isError
  } = useCategoryQuizzes(criteria, subject, currentQuizId);

  // useCategoryQuizzes hook에서 이미 현재 퀴즈 제외 처리됨
  const quizzes = data?.data || [];

  // API 에러 발생 시 섹션 숨김 (카테고리 조회 실패 등)
  if (isError) {
    logger.debug('deatil_category_quizzes_api_error', {
      criteria: JSON.stringify(criteria),
      subject,
      currentQuizId
    });
    return null;
  }

  // 퀴즈 클릭 핸들러
  const handleQuizClick = (quizId: string) => {
    logger.track('enter_detail_user', {
      location: 'category-quizzes',
      id: quizId
    });
    void router.push(`/quiz/${quizId}`);
  };

  // 전체 보기 핸들러
  const handleViewAll = () => {
    const firstQuiz = quizzes[0];
    const categoryInfo = firstQuiz?.categoryInfo;
    if (categoryInfo) {
      // Public Page와 동일한 로직: subSubject가 있으면 subject + subSubject 조합
      const subjectCode = categoryInfo.subSubject ? categoryInfo.subject + categoryInfo.subSubject : categoryInfo.subject || '';

      // schoolType을 숫자 문자열("2")에서 SchoolType 문자열("MIDDLE")로 변환
      const schoolTypeNumber = parseInt(categoryInfo.schoolType, 10);
      const schoolTypeName = SchoolMapper.getSchoolTypeNameById(schoolTypeNumber).toUpperCase();

      // 학년이 없는 경우 (유치원, 대학교 등) 학년 필터를 적용하지 않음
      // schoolGrade가 nullish한 경우 제외
      const gradeValue = categoryInfo.schoolGrade;
      const numericGrade = Number(gradeValue);
      const hasValidGrade = !!numericGrade;
      setCategoryState(prev => ({
        ...prev,
        schoolType: schoolTypeName,
        subject: subjectCode,
        grades: hasValidGrade ? [categoryInfo.schoolGrade] : [],
        semesters: []
      }));
    }
    logger.track('view_all_click', {
      location: 'category-quizzes',
      schoolType: categoryInfo?.schoolType,
      subject: categoryInfo?.subject,
      subSubject: categoryInfo?.subSubject
    });
    void router.push('/public');
  };
  if (isLoading) return null;

  // 퀴즈가 0개이면 섹션 숨김
  if (quizzes.length === 0) {
    return null;
  }
  return <section className="flex w-full flex-col gap-md" data-sentry-component="CategoryQuizzesSection" data-sentry-source-file="CategoryQuizzesSection.tsx">
      {/* 섹션 헤더 - 제목 + 전체 보기 링크 */}
      <div className="flex h-[44px] items-center justify-between gap-[10px]">
        <h2 className="text-heading-lg-extrabold text-default">
          {categoryTitle}
        </h2>
        <Button size="sm" variant="transparent" intent="neutral" onClick={handleViewAll} rightSlot={<ChevronRightIcon size="xs" />} data-sentry-element="Button" data-sentry-source-file="CategoryQuizzesSection.tsx">
          {t('detail.categoryQuizzes.viewAll')}
        </Button>
      </div>

      {/* 성공 상태 - 퀴즈 목록 */}

      <div className="flex flex-col">
        {quizzes.map((quiz, index) => <QuizListItemWithActions key={quiz.id} quiz={quiz} isLastItem={index === quizzes.length - 1} onQuizClick={handleQuizClick} />)}
      </div>
    </section>;
};